import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Tv, FileText, Calendar, Mic, LogOut, Plus, Trash2, Edit2,
  Check, X, Eye, Clock, Users, AlertCircle, ChevronRight, Settings, Search
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [shows, setShows] = useState([]);
  const [stories, setStories] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form states
  const [showForm, setShowForm] = useState({
    title: '', description: '', thumbnail: '', youtubeId: '',
    category: 'New Episode', duration: '', views: '0', isPublished: true
  });
  const [scheduleForm, setScheduleForm] = useState({
    showId: '', airDate: '', airTime: '', channel: 'Web', notes: ''
  });
  const [episodeForm, setEpisodeForm] = useState({
    title: '', description: '', duration: '', audioUrl: '', publishDate: '', isPublished: true
  });
  const [editingShow, setEditingShow] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const authString = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${authString}` };
  }, [credentials]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await axios.post(`${API}/admin/login`, {}, {
        headers: getAuthHeaders()
      });
      setIsAuthenticated(true);
      localStorage.setItem('adminCredentials', JSON.stringify(credentials));
    } catch (error) {
      setLoginError('Invalid credentials. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
    localStorage.removeItem('adminCredentials');
  };

  // Load saved credentials on mount
  useEffect(() => {
    const saved = localStorage.getItem('adminCredentials');
    if (saved) {
      const creds = JSON.parse(saved);
      setCredentials(creds);
      // Auto-login with saved credentials
      axios.post(`${API}/admin/login`, {}, {
        headers: { Authorization: `Basic ${btoa(`${creds.username}:${creds.password}`)}` }
      }).then(() => setIsAuthenticated(true)).catch(() => localStorage.removeItem('adminCredentials'));
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      fetchShows();
      fetchStories();
      fetchSchedules();
      fetchEpisodes();
    }
  }, [isAuthenticated]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchShows = async () => {
    try {
      const res = await axios.get(`${API}/admin/shows`, { headers: getAuthHeaders() });
      setShows(res.data);
    } catch (error) {
      console.error('Error fetching shows:', error);
    }
  };

  const fetchStories = async () => {
    try {
      const res = await axios.get(`${API}/admin/stories`, { headers: getAuthHeaders() });
      setStories(res.data);
    } catch (error) {
      console.error('Error fetching stories:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      const res = await axios.get(`${API}/admin/schedule`, { headers: getAuthHeaders() });
      setSchedules(res.data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const fetchEpisodes = async () => {
    try {
      const res = await axios.get(`${API}/admin/podcast/episodes`, { headers: getAuthHeaders() });
      setEpisodes(res.data);
    } catch (error) {
      console.error('Error fetching episodes:', error);
    }
  };

  // Show CRUD
  const handleCreateShow = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingShow) {
        await axios.put(`${API}/admin/shows/${editingShow}`, showForm, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/admin/shows`, showForm, { headers: getAuthHeaders() });
      }
      setShowForm({ title: '', description: '', thumbnail: '', youtubeId: '', category: 'New Episode', duration: '', views: '0', isPublished: true });
      setEditingShow(null);
      fetchShows();
      fetchStats();
    } catch (error) {
      console.error('Error saving show:', error);
    }
    setLoading(false);
  };

  const handleDeleteShow = async (id) => {
    if (!window.confirm('Are you sure you want to delete this show?')) return;
    try {
      await axios.delete(`${API}/admin/shows/${id}`, { headers: getAuthHeaders() });
      fetchShows();
      fetchStats();
    } catch (error) {
      console.error('Error deleting show:', error);
    }
  };

  const handleEditShow = (show) => {
    setShowForm({
      title: show.title,
      description: show.description || '',
      thumbnail: show.thumbnail,
      youtubeId: show.youtubeId,
      category: show.category,
      duration: show.duration,
      views: show.views,
      isPublished: show.isPublished
    });
    setEditingShow(show.id);
    setActiveTab('shows');
  };

  // Story status update
  const handleUpdateStoryStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/stories/${id}/status?status=${status}`, {}, { headers: getAuthHeaders() });
      fetchStories();
      fetchStats();
    } catch (error) {
      console.error('Error updating story:', error);
    }
  };

  const handleDeleteStory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this story?')) return;
    try {
      await axios.delete(`${API}/admin/stories/${id}`, { headers: getAuthHeaders() });
      fetchStories();
      fetchStats();
    } catch (error) {
      console.error('Error deleting story:', error);
    }
  };

  // Schedule CRUD
  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/schedule`, scheduleForm, { headers: getAuthHeaders() });
      setScheduleForm({ showId: '', airDate: '', airTime: '', channel: 'Web', notes: '' });
      fetchSchedules();
      fetchStats();
    } catch (error) {
      console.error('Error creating schedule:', error);
    }
    setLoading(false);
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    try {
      await axios.delete(`${API}/admin/schedule/${id}`, { headers: getAuthHeaders() });
      fetchSchedules();
      fetchStats();
    } catch (error) {
      console.error('Error deleting schedule:', error);
    }
  };

  // Podcast episode CRUD
  const handleCreateEpisode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/podcast/episodes`, episodeForm, { headers: getAuthHeaders() });
      setEpisodeForm({ title: '', description: '', duration: '', audioUrl: '', publishDate: '', isPublished: true });
      fetchEpisodes();
      fetchStats();
    } catch (error) {
      console.error('Error creating episode:', error);
    }
    setLoading(false);
  };

  const handleDeleteEpisode = async (id) => {
    if (!window.confirm('Are you sure you want to delete this episode?')) return;
    try {
      await axios.delete(`${API}/admin/podcast/episodes/${id}`, { headers: getAuthHeaders() });
      fetchEpisodes();
      fetchStats();
    } catch (error) {
      console.error('Error deleting episode:', error);
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center pt-20">
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Settings size={32} className="text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
            <p className="text-gray-500 mt-2">Enter your credentials to access the admin panel</p>
          </div>

          {loginError && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <AlertCircle size={20} />
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold transition-colors"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-100 pt-20">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-gray-900 min-h-screen fixed left-0 top-20 p-4">
          <nav className="space-y-2">
            {[
              { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
              { id: 'shows', icon: Tv, label: 'Shows' },
              { id: 'stories', icon: FileText, label: 'Stories' },
              { id: 'schedule', icon: Calendar, label: 'Schedule' },
              { id: 'podcast', icon: Mic, label: 'Podcast' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === item.id
                    ? 'bg-red-600 text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <item.icon size={20} />
                {item.label}
              </button>
            ))}
            <hr className="border-gray-700 my-4" />
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <LogOut size={20} />
              Logout
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="ml-64 flex-1 p-8">
          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard</h1>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {[
                  { label: 'Total Shows', value: stats?.totalShows || 0, icon: Tv, color: 'bg-blue-500' },
                  { label: 'Total Stories', value: stats?.totalStories || 0, icon: FileText, color: 'bg-green-500' },
                  { label: 'Pending Stories', value: stats?.pendingStories || 0, icon: Clock, color: 'bg-yellow-500' },
                  { label: 'Podcast Episodes', value: stats?.totalEpisodes || 0, icon: Mic, color: 'bg-purple-500' },
                ].map((stat, i) => (
                  <div key={i} className="bg-white rounded-xl p-6 shadow-md">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">{stat.label}</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
                      </div>
                      <div className={`${stat.color} p-3 rounded-lg`}>
                        <stat.icon size={24} className="text-white" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Stories */}
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Stories</h2>
                  <div className="space-y-3">
                    {stories.slice(0, 5).map((story) => (
                      <div key={story.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{story.name}</p>
                          <p className="text-sm text-gray-500">{story.storyType}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          story.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          story.status === 'approved' ? 'bg-green-100 text-green-700' :
                          story.status === 'featured' ? 'bg-purple-100 text-purple-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {story.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Upcoming Schedule */}
                <div className="bg-white rounded-xl p-6 shadow-md">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Upcoming Schedule</h2>
                  <div className="space-y-3">
                    {schedules.slice(0, 5).map((schedule) => {
                      const show = shows.find(s => s.id === schedule.showId);
                      return (
                        <div key={schedule.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{show?.title || 'Unknown Show'}</p>
                            <p className="text-sm text-gray-500">{schedule.airDate} at {schedule.airTime}</p>
                          </div>
                          <span className="text-sm text-gray-400">{schedule.channel}</span>
                        </div>
                      );
                    })}
                    {schedules.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No scheduled shows</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Shows Management */}
          {activeTab === 'shows' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-8">Manage Shows</h1>
              
              {/* Add/Edit Show Form */}
              <div className="bg-white rounded-xl p-6 shadow-md mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {editingShow ? 'Edit Show' : 'Add New Show'}
                </h2>
                <form onSubmit={handleCreateShow} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={showForm.title}
                      onChange={(e) => setShowForm({ ...showForm, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <select
                      value={showForm.category}
                      onChange={(e) => setShowForm({ ...showForm, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                    >
                      {['New Episode', 'Special', 'Drama', 'Documentary', 'Investigation', 'Exclusive'].map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail URL *</label>
                    <input
                      type="url"
                      value={showForm.thumbnail}
                      onChange={(e) => setShowForm({ ...showForm, thumbnail: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">YouTube Video ID *</label>
                    <input
                      type="text"
                      value={showForm.youtubeId}
                      onChange={(e) => setShowForm({ ...showForm, youtubeId: e.target.value })}
                      placeholder="e.g., dQw4w9WgXcQ"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration *</label>
                    <input
                      type="text"
                      value={showForm.duration}
                      onChange={(e) => setShowForm({ ...showForm, duration: e.target.value })}
                      placeholder="e.g., 45:00"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Views</label>
                    <input
                      type="text"
                      value={showForm.views}
                      onChange={(e) => setShowForm({ ...showForm, views: e.target.value })}
                      placeholder="e.g., 1.2M"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={showForm.description}
                      onChange={(e) => setShowForm({ ...showForm, description: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none resize-none"
                    />
                  </div>
                  <div className="md:col-span-2 flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showForm.isPublished}
                        onChange={(e) => setShowForm({ ...showForm, isPublished: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Published</span>
                    </label>
                    <div className="flex-1" />
                    {editingShow && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingShow(null);
                          setShowForm({ title: '', description: '', thumbnail: '', youtubeId: '', category: 'New Episode', duration: '', views: '0', isPublished: true });
                        }}
                        className="px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
                    >
                      <Plus size={20} />
                      {editingShow ? 'Update Show' : 'Add Show'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Shows List */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Show</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Category</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Duration</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {shows.map((show) => (
                      <tr key={show.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <img src={show.thumbnail} alt={show.title} className="w-16 h-10 object-cover rounded" />
                            <span className="font-medium text-gray-900">{show.title}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-500">{show.category}</td>
                        <td className="px-6 py-4 text-gray-500">{show.duration}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${show.isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                            {show.isPublished ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button onClick={() => handleEditShow(show)} className="text-blue-600 hover:text-blue-800 p-2">
                            <Edit2 size={18} />
                          </button>
                          <button onClick={() => handleDeleteShow(show.id)} className="text-red-600 hover:text-red-800 p-2">
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {shows.length === 0 && (
                  <p className="text-gray-500 text-center py-8">No shows yet. Add your first show above.</p>
                )}
              </div>
            </div>
          )}

          {/* Stories Management */}
          {activeTab === 'stories' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-8">Manage Stories</h1>
              
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Contact</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {stories.map((story) => (
                      <tr key={story.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900">{story.name}</p>
                          <p className="text-sm text-gray-500 max-w-xs truncate">{story.story}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-gray-900">{story.email}</p>
                          <p className="text-sm text-gray-500">{story.phone}</p>
                        </td>
                        <td className="px-6 py-4 text-gray-500">{story.storyType}</td>
                        <td className="px-6 py-4">
                          <select
                            value={story.status}
                            onChange={(e) => handleUpdateStoryStatus(story.id, e.target.value)}
                            className={`px-3 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${
                              story.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              story.status === 'approved' ? 'bg-green-100 text-green-700' :
                              story.status === 'featured' ? 'bg-purple-100 text-purple-700' :
                              'bg-red-100 text-red-700'
                            }`}
                          >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="featured">Featured</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button onClick={() => handleDeleteStory(story.id)} className="text-red-600 hover:text-red-800 p-2">
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {stories.length === 0 && (
                  <p className="text-gray-500 text-center py-8">No stories submitted yet.</p>
                )}
              </div>
            </div>
          )}

          {/* Schedule Management */}
          {activeTab === 'schedule' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-8">Schedule Shows</h1>
              
              {/* Add Schedule Form */}
              <div className="bg-white rounded-xl p-6 shadow-md mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Schedule a Show</h2>
                <form onSubmit={handleCreateSchedule} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Show *</label>
                    <select
                      value={scheduleForm.showId}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, showId: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                      required
                    >
                      <option value="">Select a show...</option>
                      {shows.map(show => (
                        <option key={show.id} value={show.id}>{show.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Air Date *</label>
                    <input
                      type="date"
                      value={scheduleForm.airDate}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, airDate: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Air Time *</label>
                    <input
                      type="time"
                      value={scheduleForm.airTime}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, airTime: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
                    <select
                      value={scheduleForm.channel}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, channel: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                    >
                      <option value="Web">Web</option>
                      <option value="YouTube">YouTube</option>
                      <option value="TV">TV</option>
                      <option value="Streaming">Streaming</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <input
                      type="text"
                      value={scheduleForm.notes}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
                      placeholder="Optional notes..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                    >
                      <Calendar size={20} />
                      Schedule
                    </button>
                  </div>
                </form>
              </div>

              {/* Schedule List */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Show</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Time</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Channel</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {schedules.map((schedule) => {
                      const show = shows.find(s => s.id === schedule.showId);
                      return (
                        <tr key={schedule.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 font-medium text-gray-900">{show?.title || 'Unknown'}</td>
                          <td className="px-6 py-4 text-gray-500">{schedule.airDate}</td>
                          <td className="px-6 py-4 text-gray-500">{schedule.airTime}</td>
                          <td className="px-6 py-4 text-gray-500">{schedule.channel}</td>
                          <td className="px-6 py-4 text-right">
                            <button onClick={() => handleDeleteSchedule(schedule.id)} className="text-red-600 hover:text-red-800 p-2">
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {schedules.length === 0 && (
                  <p className="text-gray-500 text-center py-8">No scheduled shows. Create a schedule above.</p>
                )}
              </div>
            </div>
          )}

          {/* Podcast Management */}
          {activeTab === 'podcast' && (
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-8">Manage Podcast Episodes</h1>
              
              {/* Add Episode Form */}
              <div className="bg-white rounded-xl p-6 shadow-md mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Episode</h2>
                <form onSubmit={handleCreateEpisode} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={episodeForm.title}
                      onChange={(e) => setEpisodeForm({ ...episodeForm, title: e.target.value })}
                      placeholder="e.g., Ep. 51: The Big Reveal"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration *</label>
                    <input
                      type="text"
                      value={episodeForm.duration}
                      onChange={(e) => setEpisodeForm({ ...episodeForm, duration: e.target.value })}
                      placeholder="e.g., 45:30"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Publish Date *</label>
                    <input
                      type="date"
                      value={episodeForm.publishDate}
                      onChange={(e) => setEpisodeForm({ ...episodeForm, publishDate: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Audio URL</label>
                    <input
                      type="url"
                      value={episodeForm.audioUrl}
                      onChange={(e) => setEpisodeForm({ ...episodeForm, audioUrl: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                    <textarea
                      value={episodeForm.description}
                      onChange={(e) => setEpisodeForm({ ...episodeForm, description: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none resize-none"
                      required
                    />
                  </div>
                  <div className="md:col-span-2 flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={episodeForm.isPublished}
                        onChange={(e) => setEpisodeForm({ ...episodeForm, isPublished: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Published</span>
                    </label>
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
                    >
                      <Plus size={20} />
                      Add Episode
                    </button>
                  </div>
                </form>
              </div>

              {/* Episodes List */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Episode</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Duration</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {episodes.map((episode) => (
                      <tr key={episode.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900">{episode.title}</p>
                          <p className="text-sm text-gray-500 max-w-md truncate">{episode.description}</p>
                        </td>
                        <td className="px-6 py-4 text-gray-500">{episode.duration}</td>
                        <td className="px-6 py-4 text-gray-500">{episode.publishDate}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${episode.isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                            {episode.isPublished ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button onClick={() => handleDeleteEpisode(episode.id)} className="text-red-600 hover:text-red-800 p-2">
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {episodes.length === 0 && (
                  <p className="text-gray-500 text-center py-8">No episodes yet. Add your first episode above.</p>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
